import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ImageStatePort, IImageInfo } from '@jax-data-science/sci-image-visualizer';
import { ProcessingImage } from '../../../models/processing-pipeline';

/**
 * Component-scoped {@link ImageStatePort} for the pipeline preview viewer.
 *
 * The library's `VisualizerComponent` renders whatever `getImageInfo$()`
 * emits. The main app binds that port to `MainState` (the selected file); here we
 * bind it to a self-contained subject driven by the pipeline's in-memory result.
 *
 * `setImage()` turns a {@link ProcessingImage} into a `blob:` URL and emits an
 * `IImageInfo` with `tiled: false`, so the OpenSeadragon backend opens it via its
 * single-image source — no tile server, no round-trip. The previous object URL is
 * revoked on every update and on destroy.
 */
@Injectable()
export class PipelineImageStateAdapter implements ImageStatePort, OnDestroy {
  private readonly imageInfo$ = new BehaviorSubject<IImageInfo | null>(null);
  private readonly loading$ = new BehaviorSubject<boolean>(false);
  private currentUrl: string | null = null;

  /** Build an IImageInfo from the in-memory image and push it to the viewer.
   *  Pass `null` to clear. `pixelSize` is the preview's physical pixel size (µm/px,
   *  already scaled for any crop by the caller); null/absent → no scale bar.
   *  Returns once the blob URL is ready. */
  async setImage(
    image: ProcessingImage | null,
    pixelSize: { mppX: number | null; mppY: number | null } | null = null,
  ): Promise<void> {
    this.revokeUrl();
    if (!image) {
      this.imageInfo$.next(null);
      return;
    }
    this.loading$.next(true);
    const blob = await image.toBlob('image/png');
    const url = URL.createObjectURL(blob);
    this.currentUrl = url;
    const isGrayscale = image.channels === 1;
    const rgbChannels = isGrayscale ? 1 : 3;
    this.imageInfo$.next({
      isGrayscale,
      // The blob is at the image's own pixel size; OSD reads that natively. Region
      // coordinates come back in these pixels — the preview component scales them
      // to full resolution for the server (see PipelinePreviewComponent).
      trueImageSize: [image.width, image.height],
      urls: [url],
      isStack: false,
      showStack: false,
      scaleRatio: true,
      fileName: 'pipeline-preview.png',
      imageMeta: [{
        channelCount: rgbChannels,
        rgbChannels,
        x: image.width,
        y: image.height,
        z: 1,
        // Physical pixel size of the preview (original µm/px scaled for the crop),
        // so OSD can draw a correct scale bar; null when the source has no mpp.
        mppX: pixelSize?.mppX ?? null,
        mppY: pixelSize?.mppY ?? null,
      }],
      tiled: false,
    });
    this.loading$.next(false);
  }

  private revokeUrl(): void {
    if (this.currentUrl) {
      URL.revokeObjectURL(this.currentUrl);
      this.currentUrl = null;
    }
  }

  ngOnDestroy(): void {
    this.revokeUrl();
  }

  // ── ImageStatePort ────────────────────────────────────────────────────
  getImageInfo$(): Observable<IImageInfo | null> { return this.imageInfo$.asObservable(); }
  isImageLoading$(): Observable<boolean> { return this.loading$.asObservable(); }
  getFilename$(): Observable<string | undefined> { return of('pipeline-preview.png'); }
  isImageCached$(): Observable<boolean> { return of(true); }
  getImageLoadingMessage$(): Observable<string> { return of(''); }
  getCacheProgress$(): Observable<number | null> { return of(null); }
  getPanelWidth$(): Observable<number> { return of(0); }
  isZoom$(): Observable<boolean> { return of(false); }

  setImageInfo(info: Partial<IImageInfo>): void { this.imageInfo$.next(info as IImageInfo); }
  setImageLoading(loading: boolean): void { this.loading$.next(loading); }
  setImageLoadingMessage(_message: string): void { /* no UI for it here */ }
  setZoom(_zoom: boolean): void { /* preview has no external zoom toggle */ }
  setImageCached(_cached: boolean): void { /* always cached (in-memory) */ }
  setLoadingError(_error: boolean): void { /* surfaced by the dialog itself */ }
  setDiagram(_diagram: unknown): void { /* host stores the component ref; unused here */ }
}
